import { decodeJWTPayload } from './jwt-utils';

export enum TokenStatus {
  VALID = 'VALID',
  EXPIRED = 'EXPIRED',
  NONE = 'NONE',
}

export interface TokenEvent {
  token: string | null;
  status: TokenStatus;
  expired?: boolean;
}

class TokenManager {
  // In-memory token storage
  private token: string | null = null;
  private tokenExpiry: number | null = null; // Store expiration timestamp
  private status: TokenStatus = TokenStatus.NONE;

  // Event listeners for token changes
  private listeners: ((event: TokenEvent) => void)[] = [];

  // Request queue management
  private refreshInProgress = false;
  private refreshPromise: Promise<string | null> | null = null;
  private pendingRequests: { resolve: (token: string) => void; reject: (error: unknown) => void }[] = [];

  constructor() {}

  // Get current token
  getToken(): string | null {
    return this.token;
  }

  // Set token with status update
  setToken(token: string | null): void {
    this.token = token;
    this.tokenExpiry = token ? this.extractTokenExpiry(token) : null;
    this.status = token ? TokenStatus.VALID : TokenStatus.NONE;
    this.notify();
  }

  // Extract expiration timestamp from JWT token
  private extractTokenExpiry(token: string): number | null {
    const payload = decodeJWTPayload(token);

    return payload?.exp || null;
  }

  // Subscribe to token changes
  subscribe(listener: (event: TokenEvent) => void): () => void {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter((l) => l !== listener);
    };
  }

  // Notify all listeners
  private notify(expired = false): void {
    const event: TokenEvent = {
      token: this.token,
      status: this.status,
      expired,
    };

    this.listeners.forEach((listener) => listener(event));
  }

  // Set token as expired
  setExpired(): void {
    this.token = null;
    this.tokenExpiry = null;
    this.status = TokenStatus.EXPIRED;
    this.notify(true);
  }

  // Clear token
  clearToken(): void {
    this.token = null;
    this.tokenExpiry = null;
    this.status = TokenStatus.NONE;
    this.notify();
  }

  // Initialize on app start - just attempt to refresh the token once
  async initialize(): Promise<boolean> {
    const token = await this.refreshToken();
    return token !== null;
  }

  // Refresh token
  async refreshToken(): Promise<string | null> {
    // If a refresh is already in progress, return the existing promise
    if (this.refreshInProgress && this.refreshPromise) {
      return this.refreshPromise;
    }

    // Create a new refresh promise
    this.refreshInProgress = true;
    this.refreshPromise = this.doRefreshToken();

    return this.refreshPromise;
  }

  // Handle request queueing when a token needs to be obtained
  async getTokenForRequest(): Promise<string> {
    // If we have a valid (non-expired) token, return it immediately
    if (this.token && !this.isTokenExpired()) {
      return this.token;
    }

    // If a refresh is already in progress, queue this request
    if (this.refreshInProgress) {
      return new Promise((resolve, reject) => {
        this.pendingRequests.push({ resolve, reject });
      });
    }

    // Start a new refresh
    const token = await this.refreshToken();
    if (!token) {
      throw new Error('Failed to obtain token');
    }
    return token;
  }

  // Process all pending requests after token refresh
  private processPendingRequests(token: string | null, error?: unknown): void {
    if (token) {
      // Resolve all pending requests with the new token
      this.pendingRequests.forEach(({ resolve }) => resolve(token));
    } else {
      // Reject all pending requests with the error
      this.pendingRequests.forEach(({ reject }) => reject(error || new Error('Failed to refresh token')));
    }

    // Clear the queue
    this.pendingRequests = [];
  }

  // Actual token refresh implementation
  private async doRefreshToken(): Promise<string | null> {
    try {
      const response = await fetch('/api/auth/refresh-token', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include', // Include cookies
        body: JSON.stringify({}),
      });

      if (!response.ok) {
        // Check specifically for 401 with REFRESH_TOKEN_EXPIRED
        if (response.status === 401) {
          const data = await response.json();
          if (data?.error?.code === 'REFRESH_TOKEN_EXPIRED') {
            this.setExpired();
          } else {
            this.clearToken();
          }
        } else {
          this.clearToken();
        }

        this.processPendingRequests(null);
        return null;
      }

      // Parse the response
      const data = await response.json();

      // Extract token from standardized response format
      let newToken = null;

      if (data?.success && data?.data?.accessToken) {
        newToken = data.data.accessToken;
      } else if (data?.accessToken) {
        newToken = data.accessToken;
      }

      if (newToken) {
        this.setToken(newToken);
        this.processPendingRequests(newToken);
        return newToken;
      }

      this.clearToken();
      this.processPendingRequests(null);
      return null;
    } catch (error) {
      this.clearToken();
      this.processPendingRequests(null, error);
      return null;
    } finally {
      this.refreshInProgress = false;
      this.refreshPromise = null;
    }
  }

  // Check if the current token is expired
  private isTokenExpired(): boolean {
    if (!this.tokenExpiry) {
      return true; // No expiry info means expired/invalid
    }

    // Check expiration (tokenExpiry is in seconds, Date.now() is in milliseconds)
    const now = Date.now() / 1000;
    return this.tokenExpiry <= now;
  }
}

// Singleton instance
export const tokenManager = new TokenManager();
